import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ArrowLeft, Sparkles, Download, RefreshCw, CheckCircle2, XCircle, Mail } from 'lucide-react';
import api from '@/utils/api';

const AdminConnectionsPage = () => {
  const { id } = useParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minScore, setMinScore] = useState(30);
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { min_score: minScore };
      if (category) params.category = category;
      if (status) params.status = status;
      const { data } = await api.get(`/admin/events/${id}/connections`, { params });
      setRows(data.suggestions || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const runGenerate = async () => {
    if (!window.confirm('Generate connection suggestions for all eligible confirmed attendees?')) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/events/${id}/connections/generate`, { min_score: 0 });
      alert(`Created: ${data.created}\nSkipped (ineligible / low score): ${data.skipped}\nAlgorithm: ${data.algorithm_version}`);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Generate failed'); }
    finally { setGenerating(false); }
  };

  const decide = async (sid, decision) => {
    let reason = '';
    if (decision === 'dismissed') reason = window.prompt('Reason (optional):') || '';
    try {
      await api.patch(`/admin/events/${id}/connections/${sid}/decision`, { decision, admin_override_reason: reason });
      setSelected(null);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };

  const genIcebreakers = async (sid) => {
    try {
      const { data } = await api.post(`/admin/events/${id}/connections/${sid}/generate-icebreakers`);
      setSelected((s) => (s?.id === sid ? { ...s, icebreakers: data.icebreakers } : s));
      alert('Icebreakers generated.');
    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };

  const download = () => {
    const token = localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${id}/connections/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => r.blob()).then((b) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = 'connections.csv';
      a.click();
    });
  };

  const approvedNotSent = rows.filter((r) => r.admin_decision === 'approved' && !r.introduction_email_sent_at).length;
  const bulkSendIntros = async () => {
    if (approvedNotSent === 0) {
      alert('No approved-and-not-yet-sent suggestions.');
      return;
    }
    if (!window.confirm(`Send introduction emails for ${approvedNotSent} approved suggestion${approvedNotSent === 1 ? '' : 's'}? Both parties will each receive an email.`)) return;
    try {
      const { data } = await api.post(`/admin/events/${id}/connections/send-introductions`);
      alert(`Sent: ${data.sent_count}\nSkipped (already sent): ${data.skipped_already_sent}\nSkipped (consent missing): ${data.skipped_missing_consent}`);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Bulk send failed'); }
  };

  return (
    <div className="p-8 space-y-6 max-w-6xl" data-testid="admin-connections-page">
      <Link to={`/admin/events/${id}/edit`} className="text-sm text-primary-700 hover:text-primary-900 flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back to event
      </Link>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900">Suggested introductions</h1>
          <p className="text-neutral-600 text-sm">Rule-based scoring, no LLM decides compatibility. Each suggestion is human-reviewable.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" onClick={download}><Download className="h-4 w-4 mr-1.5" /> CSV</Button>
          <Button variant="outline" onClick={bulkSendIntros} disabled={approvedNotSent === 0}
                  data-testid="bulk-send-intros-btn"
                  className="border-secondary-400 text-secondary-800 hover:bg-secondary-50">
            <Mail className="h-4 w-4 mr-1.5" /> Send introductions{approvedNotSent > 0 ? ` (${approvedNotSent})` : ''}
          </Button>
          <Button onClick={runGenerate} disabled={generating} className="bg-primary-800 hover:bg-primary-900 text-white" data-testid="connections-generate-btn">
            <Sparkles className="h-4 w-4 mr-1.5" /> {generating ? 'Generating...' : 'Generate suggestions'}
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <Input type="number" value={minScore} onChange={(e) => setMinScore(Number(e.target.value))} className="w-24" placeholder="Min score" />
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 px-3 rounded-md border border-neutral-200 text-sm" data-testid="connections-cat-filter">
          <option value="">All categories</option>
          <option value="friendship">Friendship</option>
          <option value="community">Community</option>
          <option value="travel_buddies">Travel buddies</option>
          <option value="marriage">Marriage</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 px-3 rounded-md border border-neutral-200 text-sm" data-testid="connections-status-filter">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="dismissed">Dismissed</option>
        </select>
        <Button variant="outline" size="sm" onClick={load}>Apply</Button>
      </div>

      <Card className="border"><CardContent className="p-0 divide-y divide-neutral-100">
        {loading ? <div className="p-10 text-center text-neutral-400">Loading...</div>
        : rows.length === 0 ? <div className="p-10 text-center text-neutral-500">No suggestions match these filters. Try clicking Generate.</div>
        : rows.map((r) => (
          <button key={r.id} onClick={() => setSelected(r)} className="w-full text-left p-4 hover:bg-neutral-50 flex items-center gap-3" data-testid={`connection-row-${r.id}`}>
            <div className="w-14 text-right">
              <div className="font-mono text-2xl font-bold text-primary-900">{r.total_score}</div>
              <div className="text-[10px] text-neutral-500">points</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-primary-900 truncate">
                {r.a?.preferred_name || r.registration_a_id.slice(0, 8)} <span className="text-neutral-400">×</span> {r.b?.preferred_name || r.registration_b_id.slice(0, 8)}
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {(r.shared_intentions || []).map((c) => (
                  <Badge key={c} variant="outline" className="text-[10px] capitalize bg-secondary-50 border-secondary-200 text-primary-900">{c.replace(/_/g, ' ')}</Badge>
                ))}
                {(r.reasons || []).slice(0, 2).map((s, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] bg-neutral-50 border-neutral-200 text-neutral-700">{s}</Badge>
                ))}
              </div>
            </div>
            <div className="text-xs">
              <Badge variant="outline" className={r.confidence === 'high' ? 'bg-success-50 border-success-200 text-success-800' : r.confidence === 'medium' ? 'bg-secondary-50 border-secondary-200' : 'bg-neutral-100'}>
                {r.confidence}
              </Badge>
            </div>
            <div className="text-xs w-24 text-right">
              {r.admin_decision ? (
                <Badge variant="outline" className={r.admin_decision === 'approved' ? 'bg-success-50 border-success-200 text-success-800' : 'bg-neutral-100 text-neutral-500'}>
                  {r.admin_decision}
                </Badge>
              ) : (
                <span className="text-neutral-400">pending</span>
              )}
              <DeliveryChip row={r} />
            </div>
          </button>
        ))}
      </CardContent></Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="connection-detail-drawer">
          {selected && (
            <>
              <SheetHeader><SheetTitle>Suggestion detail</SheetTitle></SheetHeader>
              <div className="mt-5 space-y-4 text-sm">
                <div className="rounded-xl border border-primary-100 bg-primary-50/40 p-4 text-center">
                  <div className="text-xs uppercase tracking-widest text-primary-700">Total shared signals</div>
                  <div className="font-display text-4xl font-bold text-primary-900 mt-1">{selected.total_score}</div>
                  <div className="text-[10px] text-neutral-500 uppercase mt-0.5">points (not a %)</div>
                </div>
                <Row k="A" v={`${selected.a?.preferred_name || '-'} (${selected.a?.ticket_code || '-'})`} />
                <Row k="B" v={`${selected.b?.preferred_name || '-'} (${selected.b?.ticket_code || '-'})`} />
                <Row k="Categories" v={(selected.shared_intentions || []).join(', ')} />
                <Row k="Confidence" v={selected.confidence} />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Reasons</div>
                  <ul className="space-y-1 text-neutral-700">
                    {(selected.reasons || []).map((r, i) => <li key={i}>• {r}</li>)}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Score breakdown</div>
                  <table className="w-full text-xs">
                    <tbody>
                      {Object.entries(selected.per_component || {}).map(([k, v]) => (
                        <tr key={k} className="border-b border-neutral-100">
                          <td className="py-1 text-neutral-600">{k.replace(/_/g, ' ')}</td>
                          <td className="py-1 text-right">{v.score} × {v.weight}% = <span className="font-mono font-semibold text-primary-900">{v.contribution}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Row k="Data used" v={(selected.data_used || []).join(', ') || '-'} />
                <Row k="Data missing" v={(selected.data_missing || []).join(', ') || 'none'} />
                <Row k="Consent relied on" v={(selected.consent_relied_on || []).join(', ')} />
                {selected.icebreakers && (Array.isArray(selected.icebreakers) ? selected.icebreakers.length > 0 : true) && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Icebreakers (Dost AI, grounded)</div>
                    {Array.isArray(selected.icebreakers) ? (
                      <ol data-testid="ice-out" className="text-xs bg-neutral-50 border border-neutral-100 rounded-md p-3 space-y-1.5 list-decimal list-inside">
                        {selected.icebreakers.map((ib, idx) => (
                          <li key={idx} data-testid={`icebreaker-item-${idx}`}>{ib}</li>
                        ))}
                      </ol>
                    ) : (
                      <pre className="whitespace-pre-wrap text-xs bg-neutral-50 border border-neutral-100 rounded-md p-3" data-testid="ice-out">{selected.icebreakers}</pre>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-6 space-y-2">
                {!selected.admin_decision && (
                  <>
                    <Button onClick={() => decide(selected.id, 'approved')} className="w-full bg-success-600 hover:bg-success-700 text-white" data-testid="approve-btn">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve introduction
                    </Button>
                    <Button variant="outline" onClick={() => decide(selected.id, 'dismissed')} className="w-full" data-testid="dismiss-btn">
                      <XCircle className="h-4 w-4 mr-1.5" /> Dismiss
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={() => genIcebreakers(selected.id)} className="w-full" data-testid="generate-icebreakers-btn">
                  <Sparkles className="h-4 w-4 mr-1.5" /> Generate icebreakers
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

/* Phase 9 P4 - Intro email delivery status chip.
 * Fallback path: chip is driven purely by introduction_email_sent_at plus
 * introduction_email_status.errors (populated by matching_routes at send time).
 * Resend webhook (delivered / bounced / complained) is a followup - if
 * introduction_email_status.delivered / .bounced fields exist we show them.
 */
const DeliveryChip = ({ row }) => {
  const decision = row.admin_decision;
  const sentAt = row.introduction_email_sent_at;
  const status = row.introduction_email_status || null;

  // Not approved yet -> no chip
  if (decision !== 'approved') return null;

  let cls = 'bg-neutral-100 text-neutral-500 border-neutral-200';
  let label = 'not sent';
  let title = 'Approved but not yet sent';

  if (sentAt) {
    // Prefer webhook-driven states if present, otherwise show 'sent'.
    if (status && status.bounced) {
      cls = 'bg-accent-50 border-accent-200 text-accent-700';
      label = 'bounced';
      title = `Bounced ${status.bounced_at || ''}`.trim();
    } else if (status && status.complained) {
      cls = 'bg-accent-50 border-accent-200 text-accent-700';
      label = 'complained';
      title = 'Recipient marked as spam';
    } else if (status && status.delivered) {
      cls = 'bg-green-50 border-green-200 text-green-700';
      label = 'delivered';
      title = `Delivered ${status.delivered_at || ''}`.trim();
    } else if (status && (status.errors || []).length > 0) {
      cls = 'bg-accent-50 border-accent-200 text-accent-700';
      label = 'send failed';
      title = (status.errors || []).join(', ');
    } else {
      cls = 'bg-secondary-50 border-secondary-200 text-secondary-800';
      label = 'sent';
      title = `Queued to Resend at ${sentAt}`;
    }
  }
  return (
    <div className="mt-1" title={title}>
      <span className={`inline-block text-[10px] uppercase tracking-widest border rounded px-1.5 py-0.5 ${cls}`}
            data-testid={`delivery-chip-${row.id}`}>
        {label}
      </span>
    </div>
  );
};


const Row = ({ k, v }) => (
  <div className="flex items-start gap-3 py-1 border-b border-neutral-100">
    <div className="text-[10px] uppercase tracking-widest text-neutral-500 w-28 shrink-0 pt-0.5">{k}</div>
    <div className="text-sm text-primary-900 flex-1 break-words">{v || '-'}</div>
  </div>
);

export default AdminConnectionsPage;
